import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { Box, Skeleton } from '@mui/material';

// Custom Components
import useApi from '@/lib/api/useApi';
import { useFullscreenImages } from '@/context/FullscreenImageContext/FullscreenImageContext';

// Types
import { FieldAny } from '@/@types/models/model';

interface IApiImageControlProps {
  field: FieldAny;
  value: unknown;
}

const IMAGE_HEIGHT = 160;

// base64 by api path, so switching GFI tabs back and forth doesn't refetch
const imageCache = new Map<string, string>();

// the api call carries the user's token, so only our own relative api paths
const isRelativeApiPath = (value: unknown): value is string =>
  typeof value === 'string' && value !== '' && !/^([a-z][a-z0-9+.-]*:|\/)/i.test(value);

const ApiImageControl: FunctionComponent<IApiImageControlProps> = (props) => {
  const { field, value } = props;
  const { t } = useTranslation();
  const api = useApi();
  const { showImages } = useFullscreenImages();

  const apiPath = isRelativeApiPath(value) ? value : null;

  const [image, setImage] = useState<string | null>(apiPath ? imageCache.get(apiPath) ?? null : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!apiPath) {
      setImage(null);
      return undefined;
    }
    const cached = imageCache.get(apiPath);
    if (cached) {
      setImage(cached);
      return undefined;
    }

    let cancelled = false;
    setImage(null);
    setLoading(true);
    api
      .get(apiPath)
      .then((resp) => {
        const b64 = resp.success && resp.data ? (resp.data as { image?: string }).image : undefined;
        if (b64) imageCache.set(apiPath, b64);
        if (!cancelled) setImage(b64 ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiPath]);

  const maxHeight = field.image_max_height ?? IMAGE_HEIGHT;
  const maxWidth = field.image_max_width ? `min(100%, ${field.image_max_width}px)` : '100%';

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={maxHeight} sx={{ maxWidth, borderRadius: 1 }} />;
  }

  if (!apiPath || !image) return null;

  const alt = field.ttoken ? t(field.ttoken) : field.source;

  return (
    <Box
      component="img"
      src={`data:image/png;base64,${image}`}
      alt={alt}
      onClick={() => showImages([{ id: field.source, name: `${field.source}.png`, type: 'image/png', size: 0, content: image }], 0, apiPath)}
      sx={{ display: 'block', width: '100%', maxWidth, maxHeight, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
    />
  );
};

export default ApiImageControl;
